import { EventEmitter, Injectable } from '@angular/core';
import { ISwal } from './iswal';
import { ToastPositionEnum } from './toast-position.enum';
import { ToastTypeEnum } from './toast-type-enum';
import { MatSnackBar } from '@angular/material/snack-bar';

export const DEFAULT_TOAST_PROPS = {
  position: ToastPositionEnum.TOP_END,
  toast: true,
  timer: 3000,
  showConfirmButton: false,
  backdrop: '',
  timerProgressBar: true,
};

export const MODAL_PROPS = {
  position: ToastPositionEnum.CENTER,
  toast: false,
  timer: null,
  showConfirmButton: true,
  backdrop: `rgba(0,0,0,0.5) left top no-repeat`,
  allowOutsideClick: false,
};

interface IProps {
  position?: ToastPositionEnum;
  showConfirmButton?: boolean;
  timer?: any;
  toast?: boolean;
  backdrop?: string;
  confirmButtonColor?: string;
  confirmButtonText?: string;
  title?: string;
  enableHtml?: boolean;
  id?: string;
  iconColor?: string;
  showDenyButton?: boolean;
  denyButtonText?: string;
  denyButtonColor?: string;
  allowOutsideClick?: boolean;
  customClass?: {
    container?: string;
    popup?: string;
    header?: string;
    title?: string;
    closeButton?: string;
    icon?: string;
    image?: string;
    content?: string;
    input?: string;
    actions?: string;
    confirmButton?: string;
    denyButton?: string;
    cancelButton?: string;
    footer?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  swalFire: EventEmitter<ISwal> = new EventEmitter();
  backdrop = `rgba(0,0,0,0.5) left top no-repeat`;

  // Propriedades padrão para toasts
  toastProps = {
    position: ToastPositionEnum.TOP_END,
    showConfirmButton: true,
    confirmButtonText: 'OK',
    timer: 3000,
    toast: true,
    backdrop: '',
    timerProgressBar: true,
  };

  // Propriedades padrão para modais
  modalProps = {
    position: ToastPositionEnum.CENTER,
    showConfirmButton: true,
    timer: null,
    toast: false,
    backdrop: this.backdrop,
    confirmButtonColor: '#173749',
    confirmButtonText: 'OK',
    allowOutsideClick: false,
  };

  constructor(private snackBar: MatSnackBar) { }

  showSuccess(
    message: string,
    props: IProps = {
      ...this.toastProps,
      title: 'Sucesso',
      timer: 3000,
      iconColor: '#4CAF50',
    }
  ) {
    this.swalFire.emit({
      ...this.getProps(props),
      text: message,
      icon: ToastTypeEnum.SUCCESS,
    });
  }

  showError(
    message: string,
    props: IProps = {
      ...this.toastProps,
      title: 'Erro',
      timer: 5000,
      iconColor: '#F44336',
      showConfirmButton: true,
      confirmButtonText: 'OK',
    }
  ) {
    this.swalFire.emit({
      ...this.getProps(props),
      text: message,
      icon: ToastTypeEnum.ERROR,
    });
  }

  showInfo(
    message: string,
    props: IProps = {
      ...this.toastProps,
      title: 'Informação',
    }
  ) {
    this.swalFire.emit({
      ...this.getProps(props),
      text: message,
      icon: ToastTypeEnum.INFO,
    });
  }

  showWarn(
    message: string,
    props: IProps = {
      ...this.toastProps,
      title: 'Atenção',
      timer: 6000,
      iconColor: '#FFC107',
      showConfirmButton: true,
      confirmButtonText: 'OK',
    },
    actionCallback?: () => void
  ) {
    const toastProps = {
      ...this.getProps(props),
      text: message,
      icon: ToastTypeEnum.WARNING,
    };

    if (actionCallback) {
      toastProps.showDenyButton = true;
      toastProps.denyButtonText = 'Continuar';
      toastProps.denyButtonColor = '#FFC107';
      toastProps.confirmButtonText = 'Cancelar';
    }

    this.swalFire.emit(toastProps);
  }

  showPermission(
    action: string,
    props: IProps = {
      ...this.modalProps,
      title: 'Aviso de Permissão',
      confirmButtonColor: '#FF9800',
      confirmButtonText: 'Entendi',
      iconColor: '#FF9800',
    }
  ) {
    this.swalFire.emit({
      ...this.getProps(props),
      text: `Você não tem permissão para ${action}.`,
      icon: ToastTypeEnum.PERMISSION,
      customClass: {
        popup: 'permission-modal',
        title: 'permission-title',
        confirmButton: 'permission-button',
      }
    });
  }

  showInternal(
    message: string = 'Erro interno do servidor. Tente novamente mais tarde.',
    props: IProps = {
      ...this.modalProps,
      title: 'Erro Interno',
      confirmButtonColor: '#9E9E9E',
      confirmButtonText: 'Fechar',
      showDenyButton: true,
      denyButtonText: 'Tentar novamente',
      denyButtonColor: '#173749',
      iconColor: '#9E9E9E',
    },
    retryCallback?: () => void
  ) {
    const toastProps = {
      ...this.getProps(props),
      text: message,
      icon: ToastTypeEnum.INTERNAL,
      customClass: {
        popup: 'internal-modal',
        title: 'internal-title',
      }
    };

    if (retryCallback) {
      toastProps.showDenyButton = true;
      toastProps.denyButtonText = 'Tentar novamente';
    }

    this.swalFire.emit(toastProps);
  }

  /**
   * Atualiza um toast informativo existente usando o ID
   * @param id ID do toast a ser atualizado
   * @param message Nova mensagem para o toast
   * @param props Propriedades adicionais
   */
  updateInfo(
    id: string,
    message: string,
    props: IProps = {
      ...this.toastProps,
      title: 'Informação',
    }
  ) {
    this.swalFire.emit({
      ...this.getProps(props),
      text: message,
      icon: ToastTypeEnum.INFO,
      id,
      update: true
    });
  }

  /**
   * Fecha um toast específico pelo ID
   * @param id ID do toast a ser fechado
   */
  closeToast(id: string) {
    this.swalFire.emit({
      id,
      close: true
    });
  }

  private getProps(props: any): any {
    // Determinar quais propriedades padrão usar com base no tipo de notificação
    const baseProps = props.toast !== false ? this.toastProps : this.modalProps;
    return { ...baseProps, ...props };
  }
}
